import { useState } from 'react';
import { useCachedAccess } from '@/lib/access/use-access';
import {
  ROUTING_PERMISSION,
  RoutingSaveError,
  canMove,
  canSetParticipation,
  draftFromView,
  isDirty,
  keepsLeader,
  moveMember,
  routingKey,
  setParticipation,
  toSaveBody,
  type RoutingDraft,
} from '@/lib/providers/provider-routing';
import type {
  RoutingRefusalReason,
  RoutingSaveErrorCode,
  RoutingStrategy,
  RoutingView,
} from '@/lib/providers/types';
import { useSaveRouting } from '@/lib/providers/use-providers';

/**
 * §14.5 — the "Order of use" editor state for ONE routed key, lifted to the
 * service card because its controls are spread across it: the strategy sits in
 * the card header, participation and up/down sit in each provider's row
 * detail, and the save bar under the header.
 *
 *  · The draft is local; nothing is sent until "Save". "Discard" returns to the
 *    saved view.
 *  · The draft belongs to ONE saved version: a save or a reload (new `version`
 *    or `source`) starts it fresh, while a re-read that only moved what SERVES
 *    keeps the operator's pending edits.
 *  · Supervisor+ only, like the on/off switch: anyone else reads the same
 *    controls disabled.
 *  · Not optimistic: the card changes when the server answers with the view as
 *    committed. A 409 means someone else saved first — the view reloads and the
 *    card says so.
 *  · The airline always wins (`view.leader`): a move or a park that would put a
 *    source above the airline provider, or let one answer in its place, is never
 *    offered (`canMove` / `canParticipate`), and is refused here too.
 */
export interface RoutingDraftApi {
  view: RoutingView;
  /** `transport-dispatch` — the id every test id of this key is built from. */
  key: string;
  draft: RoutingDraft;
  reason: string;
  dirty: boolean;
  busy: boolean;
  canEdit: boolean;
  /** `canEdit` and not saving right now. */
  editable: boolean;
  /** Strategies the key allows (the one in force first if the server stopped allowing it). */
  options: RoutingStrategy[];
  notice: 'saved' | 'conflict' | null;
  /** What to tell the operator about a refused save: the domain's reason when it gave one. */
  errorCode: RoutingSaveErrorCode | RoutingRefusalReason | null;
  /** The move keeps the order within bounds and the airline first. */
  canMove: (vendor: string, delta: -1 | 1) => boolean;
  /** Parking / unparking keeps the airline provider taking part where it must. */
  canParticipate: (vendor: string, enabled: boolean) => boolean;
  setStrategy: (strategy: RoutingStrategy) => void;
  move: (vendor: string, delta: -1 | 1) => void;
  setParticipates: (vendor: string, enabled: boolean) => void;
  setReason: (reason: string) => void;
  discard: () => void;
  save: () => void;
}

interface DraftState {
  base: string;
  draft: RoutingDraft;
  reason: string;
}

export function useRoutingDraft(view: RoutingView): RoutingDraftApi {
  // The permission, not a role: whoever holds `providers.toggle` (supervisor, config
  // manager, admin…). Read from the access already loaded — same as the mode choice.
  const canEdit = useCachedAccess().can(ROUTING_PERMISSION);
  const mutation = useSaveRouting(view);
  const [notice, setNotice] = useState<'saved' | 'conflict' | null>(null);

  const base = `${view.source}:${view.version ?? 0}`;
  const [state, setState] = useState<DraftState>(() => ({ base, draft: draftFromView(view), reason: '' }));
  let current = state;
  if (state.base !== base) {
    // A new saved version: the draft starts again from it (React re-renders at once).
    current = { base, draft: draftFromView(view), reason: '' };
    setState(current);
  }
  const { draft, reason } = current;

  const busy = mutation.isPending;
  const editable = canEdit && !busy;
  const dirty = isDirty(draft, view);
  const options: RoutingStrategy[] = view.allowedStrategies.includes(view.strategy)
    ? view.allowedStrategies
    : [view.strategy, ...view.allowedStrategies];

  const edit = (next: RoutingDraft): void => {
    // The airline always wins: an edit that breaks the rule is not a draft (the server refuses it).
    if (!keepsLeader(next, view.leader) && keepsLeader(draft, view.leader)) return;
    setState((s) => ({ ...s, draft: next }));
    if (notice) setNotice(null);
    if (mutation.isError) mutation.reset();
  };

  const indexOf = (vendor: string): number => draft.members.findIndex((m) => m.vendor === vendor);

  const errorCode: RoutingSaveErrorCode | RoutingRefusalReason | null =
    mutation.error instanceof RoutingSaveError
      ? mutation.error.code === 'version_conflict'
        ? null
        : (mutation.error.reason ?? mutation.error.code)
      : mutation.error
        ? 'unavailable'
        : null;

  return {
    view,
    key: routingKey(view),
    draft,
    reason,
    dirty,
    busy,
    canEdit,
    editable,
    options,
    notice,
    errorCode,
    canMove: (vendor, delta) => canMove(draft, view.leader, vendor, delta),
    canParticipate: (vendor, enabled) => canSetParticipation(draft, view.leader, vendor, enabled),
    setStrategy: (strategy) => edit({ ...draft, strategy }),
    move: (vendor, delta) => edit(moveMember(draft, indexOf(vendor), delta)),
    setParticipates: (vendor, enabled) => edit(setParticipation(draft, vendor, enabled)),
    setReason: (next) => setState((s) => ({ ...s, reason: next })),
    discard: () => {
      setState((s) => ({ ...s, draft: draftFromView(view), reason: '' }));
      mutation.reset();
    },
    save: () => {
      setNotice(null);
      mutation.mutate(toSaveBody(draft, view, reason), {
        onSuccess: () => setNotice('saved'),
        onError: (err) => {
          if (err instanceof RoutingSaveError && err.code === 'version_conflict') setNotice('conflict');
        },
      });
    },
  };
}
